import React from 'react';

import { UseFormReturn } from 'react-hook-form';

import { CompanyCreateApiDTO, CompanyUpdateApiDTO, ConstantArray, Paths, PathValue } from '_lib/api/apiTypes.shim';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { Stack } from '_lib/components/Stack/Stack';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';

export const companyConfigNotesFormValues = [
  'callCenter.notes',
  'callCenter.additionalNotes',
  'callCenter.serviceAreaNotes',
  'callCenter.dispatchRules',
  'callCenter.bigTicketItems',
  'callCenter.bigTicketItemRules',
  'callCenter.workNotToBookRules',
  'callCenter.workToBookRules',
  'callCenter.serviceFeeWaiverRules',
  'callCenter.warrantyRules',
  'callCenter.currentCoupons',
  'callCenter.discountsOffered'
] satisfies ConstantArray<Paths<CompanyUpdateApiDTO | CompanyCreateApiDTO>>;

export type CompanyConfigNotesFormValue = (typeof companyConfigNotesFormValues)[number];

export type CompanyConfigNotesFormValues = {
  [K in CompanyConfigNotesFormValue]: PathValue<CompanyUpdateApiDTO | CompanyCreateApiDTO, K>;
};

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 */
type CompanyConfigNotesFormProps<T extends CompanyConfigNotesFormValues> = {
  companyConfigNotesFormContext: T extends CompanyConfigNotesFormValues ? UseFormReturn<T> : never;
  disabled?: boolean;
};

export const CompanyConfigNotesForm = <T extends CompanyConfigNotesFormValues>(
  props: CompanyConfigNotesFormProps<T>
) => {
  const { companyConfigNotesFormContext, disabled } = props;
  const { control } = companyConfigNotesFormContext;

  return (
    <Stack useFlexGap spacing={3} sx={{ width: '100%' }}>
      <FlexCard>
        <PrimaryCardHeader title='General Notes' />
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          <TextFieldFormItem
            name='callCenter.notes'
            label='Notes'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.additionalNotes'
            label='Value'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.serviceAreaNotes'
            label='Service Area Notes'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader title='Scheduling & Dispatch' />
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          <TextFieldFormItem
            name='callCenter.dispatchRules'
            label='Dispatch Rules'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.bigTicketItems'
            label='Big Ticket Items'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.bigTicketItemRules'
            label='Big Ticket Item Scheduling Rules'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader title='Booking Rules' />
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          <TextFieldFormItem
            name='callCenter.workNotToBookRules'
            label='Work Type/Services NOT to Book'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.workToBookRules'
            label='Work Type/Services to Book'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader title='Fees & Discounts' />
        <Stack useFlexGap spacing={2} sx={{ padding: 2, width: '100%' }}>
          <TextFieldFormItem
            name='callCenter.serviceFeeWaiverRules'
            label='Fee Waiver Rules'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.warrantyRules'
            label='Warranty Handling'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.currentCoupons'
            label='Current Coupons'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
          <TextFieldFormItem
            name='callCenter.discountsOffered'
            label='Discounts Offered'
            control={control}
            multiline
            minRows={2}
            maxRows={50}
            fullWidth
            disabled={disabled}
          />
        </Stack>
      </FlexCard>
    </Stack>
  );
};
